import { cn } from '../../styles/lib/utils';
import { formatFileSize } from '../../utils/formatFileSize';
import { Badge } from '../Badge';
import { Icon } from '../Icon';
import { SvgIcon } from '../SvgIcon';
import { Text } from '../Text';

export type FileItemData = {
  id: string;
  name: string;
  size: number; // in bytes
  type?: string; // MIME type
  path?: string; // URL or path for download
};

type FileItemProps = {
  file: FileItemData;
  onDownload?: (file: FileItemData) => void;
  onDelete?: (file: FileItemData) => void;
  className?: string;
  canDownload?: boolean;
  canDelete?: boolean;
};

/**
 * FileItem displays a single file with icon, name, size, type badge, and action buttons.
 * By default, the component is read-only (no action buttons).
 */
function FileItem({ file, onDownload, onDelete, className, canDownload = false, canDelete = false }: FileItemProps) {
  const getFileExtension = (fileName: string): string => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  };

  const getFileTypeFromMime = (mimeType?: string): string => {
    if (!mimeType) return getFileExtension(file.name);
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('image')) return 'IMAGE';
    if (mimeType.includes('video')) return 'VIDEO';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'DOC';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'XLS';
    return getFileExtension(file.name);
  };

  const fileType = getFileTypeFromMime(file.type);
  const truncatedName = file.name.length > 40 ? `${file.name.substring(0, 40)}...` : file.name;

  return (
    <div
      className={cn(
        'flex items-center gap-3 border border-grey-30 rounded-[8px] h-16 px-3 py-2 relative overflow-hidden',
        className,
      )}
    >
      {/* File Icon */}
      <div className="w-12 h-12 shrink-0 flex items-center justify-center text-primary">
        <SvgIcon icon="file" size={48} />
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <Text className="text-sm font-medium text-primary truncate">{truncatedName}</Text>
        <div className="flex items-center gap-2">
          <Badge className="min-w-fit">{fileType}</Badge>
          <Text className="text-xs font-medium text-primary">{formatFileSize(file.size)}</Text>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-2 shrink-0">
        {(canDownload || canDelete) && onDownload && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(file);
            }}
            className="w-4 h-4 flex items-center justify-center text-grey-60 hover:text-primary transition-colors cursor-pointer focus-ring"
            aria-label="Download file"
          >
            <Icon icon="download" size={16} />
          </button>
        )}
        {canDelete && onDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file);
            }}
            className="w-4 h-4 flex items-center justify-center text-grey-60 hover:text-red-50 transition-colors cursor-pointer focus-ring"
            aria-label="Delete file"
          >
            <Icon icon="trash" size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export { FileItem };
export type { FileItemProps };
